import type { ReactNode } from "react";
import { Badge, type BadgeTone } from "./badge";
import { cn } from "./cn";
import { Icon } from "./icon";

// The one status badge of the console. A status is a glyph AND a word: it never depends on colour alone. The glyph is decoration for the
// eye (`aria-hidden`); the word is what a screen reader reads.

export type StatusTone = Exclude<BadgeTone, "outline">;

const glyphs: Record<StatusTone, ReactNode> = {
  neutral: <Icon name="circle" className="size-2 fill-current" />,
  success: <Icon name="checkmark-circle-02" />,
  warning: <Icon name="alert-02" />,
  danger: <Icon name="cancel-circle" />,
  info: <Icon name="information-circle" />,
  brass: <Icon name="sparkles" />,
};

type StatusBadgeProps = Readonly<{
  tone: StatusTone;
  /** The word. Required: it is the status. */
  children: ReactNode;
  /** Replaces the glyph of the tone, for a status that has its own symbol. */
  glyph?: ReactNode;
  className?: string;
}>;

export function StatusBadge({ tone, children, glyph, className }: StatusBadgeProps) {
  return (
    <Badge tone={tone} className={cn(className)}>
      <span aria-hidden="true" className="inline-flex">
        {glyph ?? glyphs[tone]}
      </span>
      {children}
    </Badge>
  );
}
